import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";

import { unpinDocument } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import { PlatformRole } from "~/config/types";
import { useUserContext } from "~/hooks/useUserContext";
import { ConfirmDialog } from "~/molecules/ConfirmDialog";
import { useDocuments } from "~/api/v2/useDocuments";
import { type DocumentSummary } from "~/api/types";
import NoResult from "~/molecules/NoResult";
import ErrorState from "~/molecules/ErrorState";
import { PinDocumentDialog } from "~/organisms/pinned/PinDocumentDialog";
import { PinnedDocumentCard } from "~/organisms/pinned/PinnedDocumentCard";
import { DashboardCard } from "~/molecules/DashboardCard";
import { dateDiff } from "~/helpers/date";

type PinDialogState =
  | { mode: "add" }
  | { mode: "edit"; document: DocumentSummary };

export const PinnedDocumentsContainer = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const { data, isLoading, mutate } = useDocuments();
  const pinnedDocuments = useMemo(
    () =>
      data
        ?.filter((d) => d.pin)
        .toSorted((a, b) => dateDiff(b.pin?.createdAt, a.pin?.createdAt)),
    [data],
  );

  const [pinDialog, setPinDialog] = useState<PinDialogState | null>(null);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [pinToRemove, setPinToRemove] = useState<DocumentSummary | null>(null);

  const canManage =
    user.role === PlatformRole.superAdmin ||
    user.role === PlatformRole.coordinator;

  // Hide container for regular users if there are no pinned documents
  if (!isLoading && pinnedDocuments?.length === 0 && !canManage) {
    return null;
  }

  const handleRemove = async () => {
    if (!pinToRemove) return;

    await toast.promise(unpinDocument(pinToRemove.id), {
      pending: t(($) => $.dashboard.pinnedDocuments.toasts.removing),
      success: t(($) => $.dashboard.pinnedDocuments.toasts.removed),
      error: t(($) => $.toasts.error.generic),
    });

    await mutate();
    setPinToRemove(null);
  };

  const title = t(($) => $.dashboard.pinnedDocuments.title);

  if (isLoading) {
    return (
      <DashboardCard title={title}>
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (pinnedDocuments === undefined) {
    return (
      <DashboardCard title={title}>
        <ErrorState />
      </DashboardCard>
    );
  }

  const button = canManage ? (
    <Button
      variant="outline"
      size="icon-sm"
      onClick={() => setPinDialog({ mode: "add" })}
    >
      <Plus />
    </Button>
  ) : null;

  return (
    <>
      <DashboardCard title={title} button={button} className="max-h-89.5">
        {pinnedDocuments.length === 0 ? (
          <NoResult />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {pinnedDocuments.map((document) => {
              return (
                <PinnedDocumentCard
                  key={document.id}
                  pinnedDocument={document}
                  canManage={canManage}
                  onEdit={() => setPinDialog({ mode: "edit", document })}
                  onRemove={() => {
                    setPinToRemove(document);
                    setIsRemoveDialogOpen(true);
                  }}
                />
              );
            })}
          </div>
        )}
      </DashboardCard>

      {canManage && (
        <>
          <PinDocumentDialog
            key={
              pinDialog?.mode === "edit"
                ? `edit-${pinDialog.document.id}`
                : "add"
            }
            open={pinDialog !== null}
            pinnedDocument={
              pinDialog?.mode === "edit" ? pinDialog.document : null
            }
            pinnedDocuments={pinnedDocuments}
            onOpenChange={(open) => {
              if (!open) setPinDialog(null);
            }}
            onSaved={() => {
              setPinDialog(null);
              void mutate();
            }}
          />

          <ConfirmDialog
            open={isRemoveDialogOpen}
            onOpenChange={setIsRemoveDialogOpen}
            title={t(($) => $.dashboard.pinnedDocuments.removeDialog.title)}
            description={t(
              ($) => $.dashboard.pinnedDocuments.removeDialog.description,
              { title: pinToRemove?.latestVersion.title ?? "" },
            )}
            confirmLabel={t(
              ($) => $.dashboard.pinnedDocuments.removeDialog.button,
            )}
            onConfirm={() => void handleRemove()}
          />
        </>
      )}
    </>
  );
};
